
import React, { useState, useEffect, useRef } from 'react';
import { useLanguage, DEFAULT_LEVEL_TITLES, DEFAULT_LEVEL_TITLES_DE } from '../i18n';
import { UserSettings, Bucket, Idea, DesignType } from '../types';
import { Settings, Save, RotateCcw, Crown, ChevronRight, Info, Database, Download, Upload, AlertTriangle, Palette, Check, Sparkles, FlaskConical } from 'lucide-react';

interface PreferencesViewProps {
  settings: UserSettings;
  buckets: Bucket[];
  ideas: Idea[];
  onSave: (settings: UserSettings) => void;
  onImport: (data: { buckets?: Bucket[], ideas?: Idea[], settings?: UserSettings }) => void;
}

interface DesignVariant {
    id: DesignType;
    name: string;
    desc: string;
    previewColor: string;
    isExperimental?: boolean;
}

interface ColorPalette {
  id: string;
  name: string;
  primary: string;
  secondary: string;
}

const FLAT_PALETTES: ColorPalette[] = [
  { id: 'sunset', name: 'Sunset Punch', primary: '#f97316', secondary: '#7e22ce' },
  { id: 'ocean', name: 'Ocean Deep', primary: '#14b8a6', secondary: '#4f46e5' },
  { id: 'cyber', name: 'Cyber Lime', primary: '#84cc16', secondary: '#d946ef' },
  { id: 'berry', name: 'Berry Blast', primary: '#f43f5e', secondary: '#7c3aed' },
  { id: 'desert', name: 'Desert Night', primary: '#f59e0b', secondary: '#06b6d4' },
];

export const PreferencesView: React.FC<PreferencesViewProps> = ({ settings, buckets, ideas, onSave, onImport }) => {
  const { t, language } = useLanguage();
  const [levelTitles, setLevelTitles] = useState<string[]>([]);
  const [currentDesign, setCurrentDesign] = useState<DesignType>('original');
  const [designColors, setDesignColors] = useState<{primary: string, secondary: string}>({
    primary: '#6366f1',
    secondary: '#ec4899'
  });
  const [activeTab, setActiveTab] = useState<'levels' | 'designs' | 'data'>('levels');
  const [message, setMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const langDefaults = language === 'de' ? DEFAULT_LEVEL_TITLES_DE : DEFAULT_LEVEL_TITLES;
    setLevelTitles(settings.levelTitles || langDefaults);
    setCurrentDesign(settings.design || 'original');
    if (settings.designColors) {
      setDesignColors(settings.designColors);
    }
  }, [settings, language]);

  const handleLevelChange = (index: number, value: string) => {
    const newTitles = [...levelTitles];
    newTitles[index] = value;
    setLevelTitles(newTitles);
  };

  const handleSave = () => {
    onSave({ ...settings, levelTitles, design: currentDesign, designColors });
    setMessage(t.preferencesSaved);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleReset = () => {
    const langDefaults = language === 'de' ? DEFAULT_LEVEL_TITLES_DE : DEFAULT_LEVEL_TITLES;
    if (confirm(t.resetToDefault + '?')) {
      setLevelTitles(langDefaults);
    }
  };

  const handleExport = () => {
    const data = {
      buckets,
      ideas,
      settings,
      version: '1.2.2',
      exportedAt: Date.now()
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lifebuckets_backup_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setMessage("Export successful");
    setTimeout(() => setMessage(''), 3000);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!confirm("This will overwrite your current buckets and tasks. Are you sure?")) {
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target?.result as string);
        onImport({
            buckets: json.buckets,
            ideas: json.ideas,
            settings: json.settings
        });
        setMessage("Import successful");
        setTimeout(() => setMessage(''), 3000);
      } catch (err) {
        alert("Failed to parse file. Make sure it's a valid LifeBuckets JSON backup.");
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const designVariants: DesignVariant[] = [
      { id: 'original', name: t.originalDesign, desc: t.originalDesignDesc, previewColor: 'bg-indigo-600' },
      { id: 'experimental_original', name: t.experimentalOriginalDesign, desc: t.experimentalOriginalDesignDesc, previewColor: 'bg-indigo-900', isExperimental: true },
      { id: 'flat', name: t.flatDesign, desc: t.flatDesignDesc, previewColor: 'bg-emerald-500' },
      { id: 'minimalist', name: t.minimalistDesign, desc: 'Coming soon: A clean, distraction-free interface.', previewColor: 'bg-slate-300' },
      { id: 'glassmorphism', name: t.glassmorphismDesign, desc: 'Coming soon: High transparency and depth effects.', previewColor: 'bg-white/20' },
      { id: 'neo_brutalism', name: t.neoBrutalismDesign, desc: 'Coming soon: Bold colors and sharp shadows.', previewColor: 'bg-yellow-400' },
  ];

  const isActivePalette = (palette: ColorPalette) => {
    return designColors.primary === palette.primary && designColors.secondary === palette.secondary;
  };

  return (
    <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center text-indigo-400">
                <Settings size={24} />
            </div>
            <div>
                <h2 className="text-2xl font-bold text-white">{t.preferences}</h2>
                <p className="text-sm text-slate-500">{t.preferencesDesc}</p>
            </div>
        </div>
        <div className="flex items-center gap-3">
             {message && (
                <span className="text-xs font-bold text-emerald-400 animate-in fade-in slide-in-from-right-4">
                    {message}
                </span>
             )}
             <button 
                onClick={handleSave}
                className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold transition-all shadow-lg shadow-indigo-600/20 active:scale-95"
             >
                <Save size={18} />
                {t.savePreferences}
             </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1 space-y-1">
              <button onClick={() => setActiveTab('levels')} className={`w-full flex items-center justify-between p-4 rounded-xl transition-all font-bold text-sm ${activeTab === 'levels' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:bg-white/5'}`}>
                  <div className="flex items-center gap-3"><Crown size={18} />{t.levelNames}</div>
                  <ChevronRight size={14} className={activeTab === 'levels' ? 'opacity-100' : 'opacity-0'} />
              </button>
              <button onClick={() => setActiveTab('designs')} className={`w-full flex items-center justify-between p-4 rounded-xl transition-all font-bold text-sm ${activeTab === 'designs' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:bg-white/5'}`}>
                  <div className="flex items-center gap-3"><Palette size={18} />{t.appDesigns}</div>
                  <ChevronRight size={14} className={activeTab === 'designs' ? 'opacity-100' : 'opacity-0'} />
              </button>
              <button onClick={() => setActiveTab('data')} className={`w-full flex items-center justify-between p-4 rounded-xl transition-all font-bold text-sm ${activeTab === 'data' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:bg-white/5'}`}>
                  <div className="flex items-center gap-3"><Database size={18} />Data</div>
                  <ChevronRight size={14} className={activeTab === 'data' ? 'opacity-100' : 'opacity-0'} />
              </button>
          </div>

          <div className="lg:col-span-3 space-y-6">
              {activeTab === 'levels' && (
                  <div className="bg-slate-900/50 backdrop-blur-sm border border-white/5 rounded-3xl p-6 sm:p-8 space-y-6 animate-in fade-in duration-300">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div>
                              <h3 className="text-lg font-bold text-white mb-1">{t.levelNames}</h3>
                              <p className="text-sm text-slate-500">{t.levelNamesDesc}</p>
                          </div>
                          <button onClick={handleReset} className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-red-500/10 text-slate-400 hover:text-red-400 rounded-lg text-xs font-bold transition-all border border-white/5"><RotateCcw size={14} />{t.resetToDefault}</button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 max-h-[60vh] overflow-y-auto pr-4 custom-scrollbar">
                          {levelTitles.map((title, idx) => (
                              <div key={idx} className="space-y-1.5">
                                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">{t.level} {idx + 1}</label>
                                  <div className="relative">
                                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600"><Crown size={12} /></div>
                                      <input type="text" value={title} onChange={(e) => handleLevelChange(idx, e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-8 pr-4 py-2.5 text-sm text-white focus:outline-none focus:border-indigo-500" placeholder={`Level ${idx + 1} Title`} />
                                  </div>
                              </div>
                          ))}
                      </div>
                  </div>
              )}

              {activeTab === 'designs' && (
                  <div className="bg-slate-900/50 backdrop-blur-sm border border-white/5 rounded-3xl p-6 sm:p-8 space-y-6 animate-in fade-in duration-300">
                      <div>
                          <h3 className="text-lg font-bold text-white mb-1">{t.appDesigns}</h3>
                          <p className="text-sm text-slate-500">{t.appDesignsDesc}</p>
                      </div>

                      <div className="grid grid-cols-1 gap-4">
                          {designVariants.map((variant) => (
                              <button 
                                key={variant.id}
                                disabled={variant.id !== 'original' && variant.id !== 'flat' && variant.id !== 'experimental_original'}
                                onClick={() => setCurrentDesign(variant.id)}
                                className={`w-full flex items-center gap-4 p-4 rounded-2xl border transition-all text-left group ${
                                    currentDesign === variant.id 
                                    ? 'bg-indigo-600/10 border-indigo-500 ring-1 ring-indigo-500 shadow-xl' 
                                    : (variant.id === 'original' || variant.id === 'flat' || variant.id === 'experimental_original')
                                        ? 'bg-white/5 border-white/5 hover:border-white/20' 
                                        : 'bg-white/5 border-white/5 opacity-50 cursor-not-allowed'
                                }`}
                              >
                                  <div className={`w-12 h-12 rounded-xl ${variant.previewColor} flex items-center justify-center shrink-0 shadow-lg relative`}>
                                      {variant.isExperimental && <FlaskConical size={14} className="absolute -top-1 -right-1 text-amber-400" />}
                                      {currentDesign === variant.id && <Check size={24} className="text-white" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                      <h4 className={`font-bold ${currentDesign === variant.id ? 'text-indigo-400' : 'text-white'}`}>
                                          {variant.name}
                                          {currentDesign === variant.id && (
                                              <span className="ml-2 text-[10px] bg-indigo-500 text-white px-1.5 py-0.5 rounded uppercase tracking-widest font-bold">Active</span>
                                          )}
                                      </h4>
                                      <p className="text-xs text-slate-500 leading-snug">{variant.desc}</p>
                                  </div>
                              </button>
                          ))}
                      </div>

                      {currentDesign === 'flat' && (
                        <div className="p-6 bg-white/5 border border-white/10 rounded-2xl space-y-6 animate-in slide-in-from-top-4 duration-500">
                           <h4 className="text-sm font-bold text-white flex items-center gap-2"><Sparkles size={16} className="text-yellow-400" /> Curated Color Moods</h4>
                           <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                              {FLAT_PALETTES.map((palette) => (
                                <button 
                                  key={palette.id}
                                  onClick={() => setDesignColors({ primary: palette.primary, secondary: palette.secondary })}
                                  className={`relative flex flex-col items-center gap-2 p-3 rounded-2xl border transition-all ${
                                    isActivePalette(palette)
                                    ? 'bg-white/10 border-indigo-500 ring-2 ring-indigo-500/50 shadow-xl scale-105'
                                    : 'bg-slate-900/50 border-white/5 hover:bg-white/5 hover:border-white/20'
                                  }`}
                                >
                                  <div className="flex -space-x-3">
                                    <div className="w-10 h-10 rounded-full border-2 border-slate-900 shadow-lg" style={{ backgroundColor: palette.primary }}></div>
                                    <div className="w-10 h-10 rounded-full border-2 border-slate-900 shadow-lg" style={{ backgroundColor: palette.secondary }}></div>
                                  </div>
                                  <span className={`text-[10px] font-bold uppercase tracking-wider text-center ${isActivePalette(palette) ? 'text-white' : 'text-slate-500'}`}>
                                    {palette.name}
                                  </span>
                                  {isActivePalette(palette) && (
                                    <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center border-2 border-slate-900 shadow-lg">
                                      <Check size={10} className="text-white" />
                                    </div>
                                  )}
                                </button>
                              ))}
                           </div>
                        </div>
                      )}
                  </div>
              )}

              {activeTab === 'data' && (
                  <div className="bg-slate-900/50 backdrop-blur-sm border border-white/5 rounded-3xl p-6 sm:p-8 space-y-8 animate-in fade-in duration-300">
                      <div>
                          <h3 className="text-lg font-bold text-white mb-1">Import & Export</h3>
                          <p className="text-sm text-slate-500">Back up your data or migrate to a new deployment.</p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="p-6 rounded-2xl bg-white/5 border border-white/5 space-y-4">
                              <div className="flex items-center gap-3 text-white"><Download className="text-emerald-400" size={20} /><span className="font-bold">Export Backup</span></div>
                              <button onClick={handleExport} className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/10"><Download size={16} />Download JSON</button>
                          </div>
                          <div className="p-6 rounded-2xl bg-white/5 border border-white/5 space-y-4">
                              <div className="flex items-center gap-3 text-white"><Upload className="text-indigo-400" size={20} /><span className="font-bold">Import Backup</span></div>
                              <button onClick={handleImportClick} className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/10"><Upload size={16} />Upload JSON</button>
                              <input type="file" ref={fileInputRef} className="hidden" accept=".json" onChange={handleFileChange} />
                          </div>
                      </div>
                  </div>
              )}
          </div>
      </div>
    </div>
  );
};
